import { RecommendButton } from '../components/Recommend';
import { DecisionLine } from '../components/DecisionLine';
import { AddonSources } from '../components/AddonSources';
import { useEffect, useState, type ReactNode } from 'react';
import { titleTintStyle, useCurrentTheme, useTitleLayout } from '../theme';
import { LayoutToggle, Reviews, useTitleColor } from '../components/TitleExtras';
import { useApp } from '../context';
import {
  mediaBadgeLabels, formatDuration, isWatched, progress, tmdbRef, versionLabel,
  type PlexItem, type PlexMedia, type PlexStream,
} from '../plex';
import { linkProps, navigate } from '../router';
import { PlayIcon } from '../components/Icons';
import { useMeta } from '../meta';
import { ThemeMusic } from '../components/ThemeMusic';
import { DiscoverRow, ParentsGuideRow, RatingsRow, TitleLogo, TrailerModal, WatchProviders } from '../components/MetaBits';
import { clearWidgetCache, CollectionRequest, RequestDialog } from '../components/Widgets';
import { dayName } from '../calendar';
import { traktRating } from '../trakt';
import { usePrefs } from '../prefs';
import { BadgeRow } from '../badges';
import { EpisodeHeatmap } from '../components/EpisodeHeatmap';
import { ArtPickerButton } from '../components/ArtPicker';
import { syncApi, watchlistApi, type ExtItem, type Meta } from '../api';

/** Detailpagina voor film, serie en aflevering. Een seizoen opent de serie met dat seizoen gekozen. */
export function Detail({ ratingKey }: { ratingKey: string }) {
  const { plex } = useApp();
  const [item, setItem] = useState<PlexItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    plex.metadata(ratingKey)
      .then((m) => {
        if (m.type === 'season' && m.parentRatingKey) {
          navigate(`/item/${m.parentRatingKey}?season=${m.ratingKey}`, { replace: true });
          return;
        }
        setItem(m);
      })
      .catch(() => setError('Kon dit item niet laden.'));
  }, [plex, ratingKey, reload]);

  if (error) return <main className="page"><p className="placeholder-row error" role="alert">{error}</p></main>;
  if (!item) return <main className="page" aria-busy="true" />;

  return (
    <>
      <ThemeMusic item={item} />
      {item.type === 'show'
        ? <ShowDetail show={item} onChanged={() => setReload((n) => n + 1)} />
        : <PlayableDetail item={item} onChanged={() => setReload((n) => n + 1)} />}
    </>
  );
}

// ---------- Film / aflevering ----------

function PlayableDetail({ item, onChanged }: { item: PlexItem; onChanged: () => void }) {
  const { plex } = useApp();
  const [mediaIndex, setMediaIndex] = useState(0);
  const media = item.Media?.[mediaIndex];
  const isEp = item.type === 'episode';
  const meta = useMeta(tmdbRef(item));
  const prefs = usePrefs();
  const [showTrailer, setShowTrailer] = useState(false);
  const logos = document.documentElement.dataset.logos !== 'off';
  const art = plex.image(item.art ?? item.grandparentArt, 1920, 1080) ?? meta?.backdrop ?? undefined;
  const p = progress(item);
  const trailer = item.Extras?.Metadata?.find((e) => (e as { subtype?: string }).subtype === 'trailer') ?? item.Extras?.Metadata?.[0];
  const ytTrailer = meta?.trailer;

  const play = (fromStart: boolean) => {
    const q = new URLSearchParams();
    if (mediaIndex) q.set('media', String(mediaIndex));
    if (!fromStart && item.viewOffset) q.set('offset', String(item.viewOffset));
    const qs = q.toString();
    navigate(`/play/${item.ratingKey}${qs ? `?${qs}` : ''}`);
  };

  const toggleWatched = async () => {
    const was = isWatched(item);
    await (was ? plex.markUnwatched(item.ratingKey) : plex.markWatched(item.ratingKey)).catch(() => undefined);
    void syncApi.watched(item.ratingKey, !was);
    onChanged();
  };

  const theme = useCurrentTheme();
  const metaLine = [
    item.year,
    formatDuration(media?.duration ?? item.duration),
    item.contentRating,
    item.audienceRating ? `★ ${item.audienceRating.toFixed(1)}` : undefined,
  ].filter(Boolean).join(' · ');

  const showDisc = !isEp && !!meta?.disc && theme?.components?.disc !== false;
  // Skins v3: cinematic titelpagina (achtergrond over de hele pagina) en de sfeerkleur van de titel
  const [layout] = useTitleLayout();
  const cine = layout === 'cinematic';
  const tint = useTitleColor(meta);
  const mainStyle = { ...(cine && art ? { ['--show-art' as string]: `url("${art}")` } : {}), ...titleTintStyle(tint, true) };

  return (
    <main className={[cine ? 'show-cinema title-cine' : '', tint ? 'title-tinted' : ''].filter(Boolean).join(' ') || undefined} style={Object.keys(mainStyle).length ? mainStyle : undefined}>
      <section className={`hero hero-detail${showDisc && !cine ? ' has-disc' : ''}`} style={art && !cine ? { backgroundImage: `url("${art}")` } : undefined}>
        {showDisc && !cine && <img className="hero-disc" src={meta!.disc!} alt="" aria-hidden="true" />}
        <div className="hero-body">
          {isEp && (
            <a className="eyebrow" {...linkProps(`/item/${item.grandparentRatingKey}?season=${item.parentRatingKey}`)}>
              {item.grandparentTitle} · Seizoen {item.parentIndex}
            </a>
          )}
          {isEp ? <h1>{`${item.index}. ${item.title}`}</h1> : <TitleLogo logo={logos ? meta?.logo : null} title={item.title} />}
          {(item.tagline ?? meta?.tagline) && <div className="tagline">{item.tagline ?? meta?.tagline}</div>}
          <div className="badges">
            <BadgeRow labels={mediaBadgeLabels(item, media)} place="title" />
            <span className="muted">{metaLine}</span>
          </div>
          <DecisionLine
            genres={isEp ? [] : (item.Genre ?? []).map((g) => g.tag)}
            remainingSec={item.type === 'movie' || isEp ? ((media?.duration ?? item.duration ?? 0) - (item.viewOffset ?? 0)) / 1000 : null}
          />
          {prefs.showRatings && <RatingsRow ratings={meta?.ratings} />}
          {prefs.showParentsGuide && <ParentsGuideRow guide={meta?.parentsGuide} />}
          {!isEp && <TraktRating refItem={tmdbRef(item)} />}
          {!isEp && <ArtPickerButton refItem={tmdbRef(item)} title={item.title} />}
          {(item.summary ?? meta?.overview) && <p>{item.summary ?? meta?.overview}</p>}

          {p !== undefined && (
            <div className="resume-bar" aria-label={`${Math.round(p * 100)}% bekeken`}>
              <div style={{ width: `${Math.round(p * 100)}%` }} />
            </div>
          )}

          <div className="hero-actions" data-focus-group>
            <button className="btn btn-primary" data-autofocus onClick={() => play(false)}>
              <PlayIcon /> {p !== undefined ? `Hervatten (${formatDuration((item.duration ?? 0) - (item.viewOffset ?? 0))} over)` : 'Afspelen'}
            </button>
            {p !== undefined && <button className="btn" onClick={() => play(true)}>Vanaf begin</button>}
            {ytTrailer ? (
              <button className="btn" onClick={() => setShowTrailer(true)}>Trailer</button>
            ) : trailer ? (
              <button className="btn" onClick={() => navigate(`/play/${trailer.ratingKey}`)}>Trailer</button>
            ) : null}
            {!isEp && <WatchlistButton guid={item.guid} />}
            {!isEp && <RecommendButton item={item} />}
            <button className="btn" onClick={toggleWatched}>{isWatched(item) ? 'Markeer als ongezien' : 'Markeer als gezien'}</button>
            <LayoutToggle />
          </div>
        </div>
      </section>

      <AddonSources item={item} />

      {(item.Media?.length ?? 0) > 1 && (
        <section className="row" aria-label="Versie">
          <h2>Versie</h2>
          <div className="filterbar" data-focus-group>
            {item.Media!.map((m, i) => (
              <button key={m.id} className="chip" aria-pressed={i === mediaIndex} onClick={() => setMediaIndex(i)}>{versionLabel(m)}</button>
            ))}
          </div>
        </section>
      )}

      <div className="detail-columns">
        {media && <TechInfo media={media} />}
        <div className="detail-meta">
          {item.Genre?.length ? <MetaLine label="Genre" value={item.Genre.map((g) => g.tag).join(', ')} /> : null}
          {meta?.directors?.length ? (
            <MetaLine label="Regie" value={<PeopleLinks people={meta.directors} />} />
          ) : item.Director?.length ? (
            <MetaLine label="Regie" value={<PeopleLinks people={item.Director.map((d) => ({ name: d.tag }))} />} />
          ) : null}
          {item.originallyAvailableAt && <MetaLine label={isEp ? 'Uitgezonden' : 'Release'} value={new Date(item.originallyAvailableAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} />}
        </div>
      </div>

      <div className="detail-providers"><WatchProviders meta={meta} /></div>

      {meta?.collection && meta.collection.parts.length > 1 && <CollectionRequest className="coll-req-detail" name={meta.collection.name} parts={meta.collection.parts} current={meta.tmdbId} />}
      <Cast roles={item.Role} meta={meta} />
      {!isEp && prefs.showReviews && <Reviews meta={meta} />}
      {meta && meta.recommendations.length > 0 && <DiscoverRow title="Meer zoals dit" items={meta.recommendations} />}
      <div className="footer-space" />
      {showTrailer && ytTrailer && <TrailerModal youtubeKey={ytTrailer.key} title={item.title} onClose={() => setShowTrailer(false)} />}
    </main>
  );
}

function MetaLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="meta-line"><span className="subtle">{label}</span><span>{value}</span></div>
  );
}

function streamList(streams: PlexStream[], type: 2 | 3): string {
  const list = streams.filter((s) => s.streamType === type).map((s) => s.displayTitle ?? s.language ?? s.codec ?? '?');
  return list.length ? list.join(', ') : '—';
}

function TechInfo({ media }: { media: PlexMedia }) {
  const part = media.Part?.[0];
  const streams = part?.Stream ?? [];
  const video = streams.find((s) => s.streamType === 1);
  const file = part?.file?.split(/[\\/]/).pop();
  return (
    <section className="tech" aria-label="Bestand en kwaliteit">
      <h2 className="tech-title">Bestand &amp; kwaliteit</h2>
      <MetaLine label="Versie" value={versionLabel(media)} />
      <MetaLine label="Video" value={video?.displayTitle ?? `${media.videoCodec ?? '?'} ${media.videoResolution ?? ''}`} />
      <MetaLine label="Audio" value={streamList(streams, 2)} />
      <MetaLine label="Ondertitels" value={streamList(streams, 3)} />
      {media.bitrate ? <MetaLine label="Bitrate" value={`${(media.bitrate / 1000).toFixed(1)} Mbps`} /> : null}
      {file && <MetaLine label="Bestand" value={file} />}
    </section>
  );
}

/** Op mijn Plex-watchlist zetten of eraf halen. */
function WatchlistButton({ guid }: { guid?: string }) {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    if (!guid?.startsWith('plex://')) return;
    watchlistApi.state(guid).then((r) => setOn(r.unknown ? null : r.onList)).catch(() => setOn(null));
  }, [guid]);
  if (!guid?.startsWith('plex://') || on === null) return null;
  const toggle = () => {
    const next = !on;
    setOn(next);
    watchlistApi.set(guid, next).then(() => clearWidgetCache()).catch(() => setOn(!next));
  };
  return (
    <button className="btn" aria-pressed={on} onClick={toggle}>
      {on ? '✓ Op mijn lijst' : '+ Mijn lijst'}
    </button>
  );
}

/** Jouw score op Trakt (1–10). Alleen zichtbaar als je Trakt hebt gekoppeld. */
function TraktRating({ refItem }: { refItem: { type: 'movie' | 'tv'; tmdbId: number } | null }) {
  const [state, setState] = useState<{ rating: number | null; linked: boolean } | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    if (!refItem) return;
    traktRating.get(refItem.type, refItem.tmdbId).then(setState).catch(() => setState(null));
  }, [refItem?.type, refItem?.tmdbId]);
  if (!refItem || !state?.linked) return null;
  const set = (n: number) => {
    const next = state.rating === n ? 0 : n; // nog eens klikken = score weghalen
    setState({ ...state, rating: next || null });
    void traktRating.set(refItem.type, refItem.tmdbId, next).catch(() => undefined);
  };
  const shown = hover ?? state.rating ?? 0;
  return (
    <div className="trakt-rating" role="radiogroup" aria-label="Jouw score op Trakt" data-focus-group onMouseLeave={() => setHover(null)}>
      <span className="subtle">Jouw score</span>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          role="radio"
          aria-checked={state.rating === n}
          aria-label={`${n} van 10`}
          className={`trakt-heart ${n <= shown ? 'is-on' : ''}`}
          onClick={() => set(n)}
          onMouseEnter={() => setHover(n)}
          onFocus={() => setHover(n)}
          onBlur={() => setHover(null)}
        >
          ♥
        </button>
      ))}
      {state.rating && <span className="trakt-value">{state.rating}/10</span>}
    </div>
  );
}

/** Namen als links naar de persoonspagina (met TMDB-id, anders zoeken op naam). */
function PeopleLinks({ people }: { people: { id?: number; name: string }[] }) {
  return (
    <span className="people-links">
      {people.map((p, i) => (
        <span key={`${p.id ?? p.name}-${i}`}>
          {i > 0 && ', '}
          <a {...linkProps(p.id ? `/person/${p.id}` : `/person?name=${encodeURIComponent(p.name)}`)}>{p.name}</a>
        </span>
      ))}
    </span>
  );
}

/**
 * Cast: uit TMDB als die er is (met acteurspagina), anders uit Plex.
 * Plex-cast heeft geen TMDB-id; die pagina zoekt dan op naam.
 */
function Cast({ roles, meta }: { roles: PlexItem['Role']; meta: Meta | null }) {
  const { plex } = useApp();
  const fromTmdb = meta?.cast?.length ? meta.cast : null;
  const list = fromTmdb
    ? fromTmdb.map((c) => ({ key: `t${c.id}`, name: c.name, role: c.character, photo: c.photo ?? undefined, href: `/person/${c.id}` }))
    : (roles ?? []).slice(0, 24).map((r, i) => ({ key: `p${i}`, name: r.tag, role: r.role ?? null, photo: plex.image(r.thumb, 160, 160), href: `/person?name=${encodeURIComponent(r.tag)}` }));
  if (!list.length) return null;
  return (
    <section className="row" aria-label="Cast">
      <h2>Cast</h2>
      <div className="row-track" data-focus-group>
        {list.map((c) => (
          <a key={c.key} className="cast" {...linkProps(c.href)} aria-label={`${c.name}${c.role ? `, ${c.role}` : ''}`}>
            <span className="cast-photo" style={c.photo ? { backgroundImage: `url("${c.photo}")` } : undefined} aria-hidden="true">
              {!c.photo && c.name.slice(0, 1)}
            </span>
            <span className="card-title">{c.name}</span>
            {c.role && <span className="card-sub">{c.role}</span>}
          </a>
        ))}
      </div>
    </section>
  );
}

// ---------- Serie ----------

function ShowDetail({ show, onChanged }: { show: PlexItem; onChanged: () => void }) {
  const { plex } = useApp();
  const [seasons, setSeasons] = useState<PlexItem[]>([]);
  const [seasonKey, setSeasonKey] = useState<string | null>(() => new URLSearchParams(location.search).get('season'));
  const [episodes, setEpisodes] = useState<PlexItem[] | null>(null);
  const meta = useMeta(tmdbRef(show));
  const prefs = usePrefs();
  const [showTrailer, setShowTrailer] = useState(false);
  const [requesting, setRequesting] = useState<ExtItem | null>(null);
  const logos = document.documentElement.dataset.logos !== 'off';
  const art = plex.image(show.art, 1920, 1080) ?? meta?.backdrop ?? undefined;
  const next = show.OnDeck?.Metadata?.[0];
  // Seizoenen die al zijn uitgezonden maar niet in Plex staan
  const today = new Date().toISOString().slice(0, 10);
  const have = new Set(seasons.map((x) => x.index));
  const missing = seasons.length && meta?.seasons ? meta.seasons.filter((x) => !have.has(x.number) && !!x.airDate && x.airDate <= today && x.episodes > 0) : [];
  const nextEp = meta?.nextEpisode;
  // Skin: afleveringen als lijst (onder elkaar) of als brede kaarten in een rij
  const episodeView = useCurrentTheme()?.components?.episodeView ?? 'list';
  // Skins v3: cinematic titelpagina = bioscoop met afleveringskaarten; plus de sfeerkleur van de titel
  const [layout] = useTitleLayout();
  const cards = episodeView !== 'list' || layout === 'cinematic';
  // Bioscoop: de achtergrond van de serie ligt onder de hele pagina, seizoenen en afleveringen erover
  const cinema = episodeView === 'cinema' || layout === 'cinematic';
  const tint = useTitleColor(meta);

  useEffect(() => {
    plex.children(show.ratingKey).then((s) => {
      setSeasons(s);
      setSeasonKey((cur) => cur ?? (next?.parentRatingKey ?? s.find((x) => (x.index ?? 0) > 0)?.ratingKey ?? s[0]?.ratingKey ?? null));
    }).catch(() => setSeasons([]));
  }, [plex, show.ratingKey, next?.parentRatingKey]);

  useEffect(() => {
    if (!seasonKey) return;
    setEpisodes(null);
    plex.children(seasonKey).then(setEpisodes).catch(() => setEpisodes([]));
  }, [plex, seasonKey]);

  const toggleWatched = async () => {
    const was = isWatched(show);
    await (was ? plex.markUnwatched(show.ratingKey) : plex.markWatched(show.ratingKey)).catch(() => undefined);
    void syncApi.watched(show.ratingKey, !was);
    onChanged();
  };

  const metaLine = [show.year, show.childCount ? `${show.childCount} seizoen${show.childCount === 1 ? '' : 'en'}` : undefined, show.contentRating, show.audienceRating ? `★ ${show.audienceRating.toFixed(1)}` : undefined]
    .filter(Boolean).join(' · ');

  return (
    <main
      className={[cinema ? 'show-cinema title-cine' : '', tint ? 'title-tinted' : ''].filter(Boolean).join(' ') || undefined}
      style={{ ...(cinema && art ? { ['--show-art' as string]: `url("${art}")` } : {}), ...titleTintStyle(tint, true) }}
    >
      <section className="hero hero-detail" style={art && !cinema ? { backgroundImage: `url("${art}")` } : undefined}>
        <div className="hero-body">
          <TitleLogo logo={logos ? meta?.logo : null} title={show.title} />
          {meta?.tagline && <div className="tagline">{meta.tagline}</div>}
          <div className="badges">
            {meta?.status && <span className={`badge ${meta.status === 'Loopt nog' ? 'badge-live' : ''}`}>{meta.status}</span>}
            <span className="muted">{metaLine}</span>
          </div>
          {nextEp && (
            <div className="next-ep">
              <span className="subtle">Volgende aflevering</span>
              <strong>{dayName(nextEp.date, true)}</strong>
              <span className="muted">S{nextEp.season} E{nextEp.episode}{nextEp.title ? ` · ${nextEp.title}` : ''}</span>
            </div>
          )}
          {prefs.showRatings && <RatingsRow ratings={meta?.ratings} />}
          {prefs.showParentsGuide && <ParentsGuideRow guide={meta?.parentsGuide} />}
          <TraktRating refItem={tmdbRef(show)} />
          <ArtPickerButton refItem={tmdbRef(show)} title={show.title} />
          {(show.summary ?? meta?.overview) && <p>{show.summary ?? meta?.overview}</p>}
          <div className="hero-actions" data-focus-group>
            {next && (
              <button
                className="btn btn-primary"
                data-autofocus
                onClick={() => navigate(`/play/${next.ratingKey}${next.viewOffset ? `?offset=${next.viewOffset}` : ''}`)}
              >
                <PlayIcon /> {next.viewOffset ? 'Hervatten' : 'Afspelen'} S{next.parentIndex} E{next.index}
              </button>
            )}
            {meta?.trailer && <button className="btn" onClick={() => setShowTrailer(true)}>Trailer</button>}
            <WatchlistButton guid={show.guid} />
            <RecommendButton item={show} />
            <button className="btn" onClick={toggleWatched}>{isWatched(show) ? 'Markeer als ongezien' : 'Alles als gezien markeren'}</button>
            <LayoutToggle />
          </div>
        </div>
      </section>

      {seasons.length > 1 && (
        <div className="filterbar" role="tablist" aria-label="Seizoenen" data-focus-group>
          {seasons.map((s) => (
            <button key={s.ratingKey} role="tab" className="chip" aria-selected={s.ratingKey === seasonKey} aria-pressed={s.ratingKey === seasonKey} onClick={() => setSeasonKey(s.ratingKey)}>
              {s.title}
            </button>
          ))}
        </div>
      )}

      {missing.length > 0 && (
        <div className="missing-seasons">
          <span>
            <strong>Niet in je bibliotheek:</strong>{' '}
            {missing.map((x) => x.name || `Seizoen ${x.number}`).join(', ')}
          </span>
          <button
            className="btn btn-sm"
            onClick={() => setRequesting({ type: 'tv', tmdbId: meta!.tmdbId, title: show.title, year: show.year ?? null, overview: show.summary ?? null, poster: meta!.poster, backdrop: meta!.backdrop, ratingKey: null })}
          >
            Aanvragen
          </button>
        </div>
      )}

      {cards ? (
        <section className="row episode-cards" aria-label="Afleveringen">
          {episodes === null && <p className="subtle">Afleveringen laden…</p>}
          <div className="row-track" data-focus-group>
            {episodes?.map((ep) => <EpisodeCard key={ep.ratingKey} ep={ep} />)}
          </div>
        </section>
      ) : (
        <section className="episodes" aria-label="Afleveringen" data-focus-group>
          {episodes === null && <p className="subtle">Afleveringen laden…</p>}
          {episodes?.map((ep) => <EpisodeRow key={ep.ratingKey} ep={ep} />)}
        </section>
      )}

      {tmdbRef(show) && (
        <EpisodeHeatmap
          tmdbId={tmdbRef(show)!.tmdbId}
          season={seasons.find((x) => x.ratingKey === seasonKey)?.index ?? null}
          episodeLink={(n) => {
            const ep = episodes?.find((x) => x.index === n);
            return ep ? `/item/${ep.ratingKey}` : null;
          }}
        />
      )}
      <Cast roles={show.Role} meta={meta} />
      {prefs.showReviews && <Reviews meta={meta} />}
      {meta && meta.recommendations.length > 0 && <DiscoverRow title="Meer zoals dit" items={meta.recommendations} />}
      <div className="footer-space" />
      {showTrailer && meta?.trailer && <TrailerModal youtubeKey={meta.trailer.key} title={show.title} onClose={() => setShowTrailer(false)} />}
      {requesting && <RequestDialog item={requesting} onClose={() => setRequesting(null)} />}
    </main>
  );
}

/** Afleveringen vanaf deze score krijgen een gouden ster (uitschieters van een serie). */
const GOLD = 9.2;

function EpisodeRow({ ep }: { ep: PlexItem }) {
  const { plex } = useApp();
  const prefs = usePrefs();
  const img = plex.image(ep.thumb, 400, 225);
  const p = progress(ep);
  // Spoilerbescherming: nog niet gezien (en niet half gekeken) → beeld vervagen, tekst verbergen
  const spoiler = prefs.hideSpoilers && !isWatched(ep) && p === undefined;
  const score = ep.audienceRating ?? ep.rating;
  return (
    <a className="episode" {...linkProps(`/item/${ep.ratingKey}`)}>
      <span className={`episode-art ${spoiler ? 'is-spoiler' : ''}`} style={img ? { backgroundImage: `url("${img}")` } : undefined} aria-hidden="true">
        {p !== undefined && <span className="progress"><span style={{ width: `${Math.round(p * 100)}%` }} /></span>}
      </span>
      <span className="episode-body">
        <span className="episode-title">
          {ep.index}. {ep.title}
          {isWatched(ep) && <span className="watched-inline" aria-label="Gezien"> ✓</span>}
        </span>
        <span className="card-sub">
          {formatDuration(ep.duration)}{ep.originallyAvailableAt ? ` · ${new Date(ep.originallyAvailableAt).toLocaleDateString('nl-NL')}` : ''}
          {score ? <> · <span className={score >= GOLD ? 'ep-score-gold' : undefined} title={score >= GOLD ? 'Een van de best beoordeelde afleveringen' : undefined}>★ {score.toFixed(1)}</span></> : null}
        </span>
        {ep.summary && (spoiler
          ? <span className="episode-summary subtle">Beschrijving verborgen tot je hem gezien hebt.</span>
          : <span className="episode-summary clamp-2">{ep.summary}</span>)}
      </span>
    </a>
  );
}

/** Brede afleveringskaart (zoals Apple TV / Plezy): beeld met titel en omschrijving erop. */
function EpisodeCard({ ep }: { ep: PlexItem }) {
  const { plex } = useApp();
  const prefs = usePrefs();
  const img = plex.image(ep.thumb, 640, 360);
  const p = progress(ep);
  const spoiler = prefs.hideSpoilers && !isWatched(ep) && p === undefined;
  const date = ep.originallyAvailableAt ? new Date(ep.originallyAvailableAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const score = ep.audienceRating ?? ep.rating;
  return (
    <a className="ep-card" {...linkProps(`/item/${ep.ratingKey}`)} aria-label={`Aflevering ${ep.index}: ${ep.title}`}>
      <span className={`ep-card-art ${spoiler ? 'is-spoiler' : ''}`} style={img ? { backgroundImage: `url("${img}")` } : undefined} aria-hidden="true" />
      <span className="ep-card-top">
        <span className="ep-chip">S{ep.parentIndex} · E{String(ep.index ?? '').padStart(2, '0')}</span>
        {date && <span className="ep-chip">{date}</span>}
        {score ? <span className={`ep-chip ${score >= GOLD ? 'ep-chip-gold' : ''}`} title={score >= GOLD ? 'Een van de best beoordeelde afleveringen' : undefined}>★ {score.toFixed(1)}</span> : isWatched(ep) ? <span className="ep-chip">✓ Gezien</span> : null}
      </span>
      <span className="ep-card-body">
        <span className="ep-card-title">{ep.title}{isWatched(ep) && score ? <span className="watched-inline" aria-label="Gezien"> ✓</span> : null}</span>
        {ep.summary && !spoiler && <span className="ep-card-summary clamp-3">{ep.summary}</span>}
        {spoiler && <span className="ep-card-summary subtle">Beschrijving verborgen tot je hem gezien hebt.</span>}
      </span>
      {p !== undefined && <span className="progress"><span style={{ width: `${Math.round(p * 100)}%` }} /></span>}
    </a>
  );
}
